import InputField from 'components/fields/InputField';
import ConfirmationModal from 'pages/Common/ConfirmationModal';
import { useEffect, useState } from 'react';
import { RiEyeCloseLine, RiEyeLine } from 'react-icons/ri';

const UpdatePasswordModal = ({
  passwordData,
  setPasswordData,
  setShowUpdateModal,
  handleUpdatePassword,
  title,
  isSubmitting,
  setIsSubmitting,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [errors, setErrors] = useState({});

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword((prev) => !prev);
  };

  const validatePassword = (password) => {
    const pattern =
      /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,15}$/;
    return pattern.test(password);
  };

  useEffect(() => {
    const newErrors = {};

    if (passwordData.password) {
      if (!validatePassword(passwordData.password)) {
        newErrors.password =
          'Password must be 8-15 characters long, include at least one uppercase letter, one number, and one special character (!@#$%^&*).';
      }
    }

    if (passwordData.password && passwordData.confirmPassword) {
      if (passwordData.password !== passwordData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
  }, [passwordData.password, passwordData.confirmPassword]);

  const handleChange = (field, value) => {
    setPasswordData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (
      Object.keys(errors).length > 0 ||
      !validatePassword(passwordData.password) ||
      passwordData.password !== passwordData.confirmPassword
    ) {
      return;
    }

    setShowConfirmation(true);
  };

  const handleConfirm = async () => {
    await handleUpdatePassword(new Event('submit'));
    setShowConfirmation(false);
  };

  return (
    <div className="bg-black fixed inset-0 z-10 flex items-center justify-center bg-opacity-50">
      <div className="relative mx-4 w-full max-w-md rounded-lg bg-white p-4 text-brand-900 shadow-lg dark:bg-navy-800 md:mx-0 md:p-8">
        <button
          type="button"
          onClick={() => setShowUpdateModal(false)}
          className="absolute right-2 top-2 h-10 w-8 rounded-full text-3xl text-brand-900 transition duration-200 dark:text-brand-900 dark:text-navy-200"
        >
          &times;
        </button>
        <h2 className="mb-4 text-lg font-medium text-navy-800 dark:text-navy-200">
          {title}
        </h2>
        <form onSubmit={handleSubmit}>
          {/* Password field with eye icon */}
          <div className="relative mb-4">
            <InputField
              label="New Password"
              type={showPassword ? 'text' : 'password'}
              value={passwordData.password}
              onChange={(e) => handleChange('password', e.target.value)}
              className="mt-1 block w-full rounded-md border-none border-gray-300 shadow-sm outline-none focus:ring focus:ring-indigo-300 sm:text-sm"
              required
              minLength={8}
              maxLength={15}
            />
            <button
              type="button"
              className="absolute right-3 top-10 text-gray-800 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
              onClick={togglePasswordVisibility}
            >
              {showPassword ? (
                <RiEyeLine size={18} />
              ) : (
                <RiEyeCloseLine size={18} />
              )}
            </button>
            {errors.password && (
              <p className="mt-2 text-sm text-red-500">{errors.password}</p>
            )}
          </div>

          {/* Confirm Password field with eye icon */}
          <div className="relative mb-4">
            <InputField
              label="Confirm New Password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={passwordData.confirmPassword}
              onChange={(e) => handleChange('confirmPassword', e.target.value)}
              className="mt-1 block w-full rounded-md border-none border-gray-300 shadow-sm outline-none focus:ring focus:ring-indigo-300 sm:text-sm"
              required
              minLength={8}
              maxLength={15}
            />
            <button
              type="button"
              className="absolute right-3 top-10 text-gray-800 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
              onClick={toggleConfirmPasswordVisibility}
            >
              {showConfirmPassword ? (
                <RiEyeLine size={18} />
              ) : (
                <RiEyeCloseLine size={18} />
              )}
            </button>
            {errors.confirmPassword && (
              <p className="mt-2 text-sm text-red-500">
                {errors.confirmPassword}
              </p>
            )}
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              className="w-40 rounded-xl bg-brand-500 py-3 pl-2.5 pr-3 text-base font-medium text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200"
              disabled={
                Object.keys(errors).length > 0 ||
                !passwordData.password ||
                !passwordData.confirmPassword ||
                passwordData.password !== passwordData.confirmPassword
              }
            >
              {isSubmitting ? 'Updating..' : '  Update Password'}
            </button>
          </div>
        </form>
      </div>
      <ConfirmationModal
        isOpen={showConfirmation}
        onClose={() => {
          if (!isSubmitting) {
            setShowConfirmation(false);
          }
        }}
        onConfirm={handleConfirm}
        title="Confirm Password Update"
        message="Are you sure you want to update this user's password?"
        confirmText={isSubmitting ? 'Updating...' : 'Yes, Update'}
        cancelText="Cancel"
        icon="info"
      />
    </div>
  );
};

export default UpdatePasswordModal;
